(() => {
const SOURCE = 'Design source / Source du dessin';
const REFERENCE = 'Design reference / Référence du dessin';
const FILE = 'Design file / Fichier du dessin';
const LEGACY_IMAGE = 'Design image / Image du dessin';
const NOTES = 'Personalization / Personnalisation';
const identityFields = new Set([SOURCE, REFERENCE, FILE, LEGACY_IMAGE, NOTES]);

function readProperties(value) {
  if (Array.isArray(value)) {
    const entries = value.map(p => Array.isArray(p)
      ? (p.length === 2 ? p : [undefined, undefined])
      : [p?.name ?? p?.key, p?.value]);
    if (entries.some(([key, val]) => typeof key !== 'string' || typeof val !== 'string')) return null;
    if (new Set(entries.map(([key]) => key)).size !== entries.length) return null;
    return Object.fromEntries(entries);
  }
  if (!value || typeof value !== 'object') return null;
  if (Object.values(value).some(v => typeof v !== 'string' && v !== null)) return null;
  return value;
}

function designIdentity(properties) {
  const props = readProperties(properties);
  if (!props) return null;
  const source = (props[SOURCE] ?? '').trim();
  const archive = source === 'CanSweet archive / Archives CanSweet';
  const own = source === 'Customer image / Image du client';
  if (!archive && !own) return null;
  const file = (props[FILE] ?? '').trim();
  const legacy = (props[LEGACY_IMAGE] ?? '').trim();
  const reference = (props[REFERENCE] ?? '').trim();
  if (archive && !reference) return null;
  if (own && !file && !legacy) return null;
  const extra = Object.entries(props)
    .filter(([key, value]) => !identityFields.has(key) && !key.startsWith('_') && value)
    .sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify([
    archive ? 'archive' : 'customer', archive ? reference : [file, legacy],
    (props[NOTES] ?? '').trim(), extra,
  ]);
}

function fixedDesignIdentity(variant, properties) {
  if (typeof variant?.designId !== 'string' || !variant.designId.trim()) return null;
  const props = properties == null ? {} : readProperties(properties);
  if (!props || [SOURCE, REFERENCE, FILE, LEGACY_IMAGE].some(key => props[key]?.trim())) return null;
  const extra = Object.entries(props).filter(([key, value]) => !key.startsWith('_') && value)
    .sort(([a], [b]) => a.localeCompare(b));
  return JSON.stringify(['fixed', variant.designId, extra]);
}

function inspectBatches(lines, catalog, minimum = 12) {
  if (!Number.isSafeInteger(minimum) || minimum < 1) throw new TypeError('Invalid minimum');
  const groups = new Map();
  const reviews = [];
  for (const line of lines) {
    const product = catalog[String(line.productId)];
    if (!product || !['cookie', 'lollipop'].includes(product.family)) continue;
    if (line.quantity === 0) continue;
    if (!Number.isSafeInteger(line.quantity) || line.quantity < 0) {
      reviews.push({kind: 'quantity_unknown', lineKeys: [line.lineKey], title: line.title});
      continue;
    }
    const variant = product.variants[String(line.variantId)];
    const design = product.designMode === 'fixed'
      ? fixedDesignIdentity(variant, line.properties) : designIdentity(line.properties);
    if (!variant || !design) {
      reviews.push({kind: !variant ? 'variant_unknown' : 'design_unknown', lineKeys: [line.lineKey], title: line.title});
      continue;
    }
    const key = JSON.stringify([String(line.productId), String(line.variantId), design]);
    const group = groups.get(key) ?? {key, title: line.title, descriptor: variant.descriptor, quantity: 0, lineKeys: []};
    group.quantity += line.quantity;
    group.lineKeys.push(line.lineKey);
    groups.set(key, group);
  }
  const batches = [...groups.values()];
  const shortages = batches.filter(batch => batch.quantity < minimum)
    .map(batch => ({...batch, kind: 'below_minimum', minimum, missing: minimum - batch.quantity}));
  return {minimum, batches, issues: [...shortages, ...reviews], needsReview: !!(shortages.length || reviews.length)};
}

function cartLines(cart) {
  if (!Array.isArray(cart?.items)) throw new TypeError('Invalid cart');
  return cart.items.map(item => ({lineKey: item.key, productId: item.product_id,
    variantId: item.variant_id, title: item.product_title ?? item.title,
    quantity: item.quantity, properties: item.properties}));
}

const wording = {
  en: {
    rule: 'Standard minimum: 12 items per design, shape and size.',
    warning: 'Smaller batches require our approval and are produced last. We may decline the order.',
    payment: 'For card payments, your bank reserves the amount first. We review your order within 1–2 days and charge your card only after accepting the order.',
    shortage: (quantity, missing, minimum) => `Your requested date may be missed. You have ${quantity} items for this design, shape and size; the standard minimum is ${minimum}. Add ${missing} to meet the standard minimum.`,
    review: 'We could not verify this item’s design or quantity. Please check its details before ordering.',
    valid: 'The displayed items meet the standard minimum.',
  },
  fr: {
    rule: 'Minimum habituel : 12 articles par dessin, forme et taille.',
    warning: 'Les petits lots nécessitent notre accord et sont fabriqués en dernier. Nous pouvons refuser la commande.',
    payment: 'Pour les paiements par carte, votre banque réserve d’abord le montant. Nous vérifions votre commande sous 1 à 2 jours et débitons votre carte uniquement après acceptation de la commande.',
    shortage: (quantity, missing, minimum) => `La date souhaitée risque de ne pas être respectée. Vous avez ${quantity} articles pour ce dessin, cette forme et cette taille ; le minimum habituel est de ${minimum}. Ajoutez-en ${missing} pour atteindre le minimum habituel.`,
    review: 'Nous n’avons pas pu vérifier le dessin ou la quantité de cet article. Veuillez vérifier ses détails avant de commander.',
    valid: 'Les articles affichés respectent le minimum habituel.',
  },
};

const propertyNames = {SOURCE, REFERENCE, FILE, LEGACY_IMAGE, NOTES};

window.CSQuantityModel = { inspectBatches, cartLines, propertyNames };
})();
